import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateAvgChart } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';
import math from '../../../util/math';
import { getStats } from './avgThroughputUtil';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import { ViewSwitch, CardList, rangeTitle } from '../../../components/CardViews/CardViews';
import legendIcon from '../../../assets/img/legend.svg';
import lineIcon from '../../../assets/img/chart-line.svg';
import chartUtils from '../../../util/chartUtils';
const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: chartUtils.CARD_TREND_COLORS,
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background: ${point.color}"></span>${point.series.name}: ${point.point.y.toFixed(2)} item${point.point.y != 1 ? 's' : ''}</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      lineWidth: 1.5,
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 3
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    }
  ]
};

const DEFAULT_POS = { x: 95, y: 85 };

export class AvgThroughput extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();

    this.state = {
      isFullSreen: false,
      isLabelsVisible: false,
      isListView: !props.wallmode,
      listRows: [],
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, config);

    if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.updateDateFormat(this.props.dateFormat);
      this.calculateChartData();
    }
    if (prevProps.dateFormat != this.props.dateFormat) {
      this.updateDateFormat(this.props.dateFormat);
    }

    if (
      prevProps.data != this.props.data ||
      prevProps.dateFormat != this.props.dateFormat ||
      prevProps.timeUnit != this.props.timeUnit
    ) {
      this.calculateChartData();
    }

    if (prevProps.details != this.props.details) {
      this.updateListRows();
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  updateFontSize = () => {
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Throughput averages',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },          
        }
      },
      legend: {
        ...chartUtils.cardLegend(this.state.isFullSreen),
        enabled: this.state.isLabelsVisible,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        labelFormatter: function () {
          return `
            <span class="color:#333333;font-size:12px;font-weight:normal">${this.name}</span>
          `;
        }
      },
      yAxis: {
        gridLineWidth: +this.state.isFullSreen,
        labels: {
          enabled: this.state.isFullSreen
        }
      }
    }, true, false, false);

    this.chart.reflow();
  };

  updateDateFormat(dateFormat) {
    const format = dateUtils.createHighchartFormats(dateFormat);
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: format.month,
          second: format.month,
          minute: format.month,
          hour: format.month,
          day: format.month,
          week: format.month,
          month: format.month
        }
      }
    });
  }

  calculateChartData() {
    const data = this.generateData(this.props.data);
    if (!data.mean.length) {
      return false;
    }

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }
    this.chart.addSeries(chartUtils.trendLineSeries({ name: 'Mean', data: data.mean, color: chartUtils.STAT_LINE_COLORS.Mean }, false), false);
    this.chart.addSeries(chartUtils.trendLineSeries({ name: 'Median', data: data.median, color: chartUtils.STAT_LINE_COLORS.Median }, false), false);
    this.chart.addSeries(chartUtils.trendLineSeries({ name: 'Mode', data: data.mode, color: chartUtils.STAT_LINE_COLORS.Mode }, false), false);

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data.mean[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
    this.updateListRows();
  }

  generateData = data => {
    return getStats(data, this.props.timeUnit, this.props.excludeWeekendOpts, this.props.excludeZeroDays);
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  updateListRows() {
    const histogram = this.props.details.series;
    const overall = histogram && histogram.length && {
      Mean: math.getMeanFromHistogramBy(histogram, 'y', 'x'),
      Median: math.getMedianFromHistogramBy(histogram, 'y', 'x'),
      Mode: math.getModeFromHistogramBy(histogram, 'y', 'x')
    };
    const format = y => `${y.toFixed(2)} item${y != 1 ? 's' : ''}`;
    const rows = (this.chart ? this.chart.series : []).map(series => ({
      name: series.name,
      color: series.color,
      values: [overall && overall[series.name] != null ? format(overall[series.name]) : '---']
    }));
    this.setState({ listRows: rows });
  }

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullSreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullSreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullSreen ? 'absolute' : ''
          }}
        >
          <div className='box_white_title'>
            <h3>Throughput averages</h3>
            <div className='right-actions'>
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'line', icon: lineIcon }]}
                active={this.state.isListView ? 'list' : 'line'}
                onChange={view => this.setState({ isListView: view === 'list' })}
              />
              <Button variant='secondary' size='sm'
                className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullSreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <div ref={this.chartContainer} />
          {this.state.isListView && <CardList columns={[rangeTitle(this.props.detailsDates || this.props.dates, this.props.dateFormat)]} rows={this.state.listRows} />}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'th')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    timeUnit: state.filters.th.timeUnit,
    dates: state.filters.dates,
    details: state.throughputHistogram.details,
    detailsDates: state.throughputHistogram.detailsDates
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(AvgThroughput));
